import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Card,
  CardContent,
  Pagination,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Flag as FlagIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Score as ScoreIcon,
} from '@mui/icons-material';
import api from './api';

const SocialTariffManager = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewData, setReviewData] = useState({
    reviewStatus: '',
    adminNotes: '',
    rejectionReason: ''
  });
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    reviewStatus: '',
    status: ''
  });
  const [reviewLoading, setReviewLoading] = useState(false);

  const statusColors = {
    PENDING: 'warning',
    APPROVED: 'success',
    DENIED: 'error',
    FLAGGED: 'info'
  };

  const applicationStatusLabels = {
    RETIRED: 'Retired',
    UNEMPLOYED: 'Unemployed',
    STUDENT: 'Student',
    SINGLE_PARENT: 'Single Parent',
    REFUGEE_MIGRATION: 'Refugee/Migration',
    CHRONIC_ILLNESS: 'Chronic Illness',
    WITH_DISABILITY: 'With Disability',
    NO_BANK_ACCOUNT: 'No Bank Account',
    ASYLUM_PROCEDURE: 'Asylum Procedure',
    OTHER: 'Other'
  };

  useEffect(() => {
    fetchApplications();
    fetchStats();
  }, [page, filters]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...filters
      });
      
      const response = await api.get(`/social-tariff/admin/applications?${params}`);
      setApplications(response.data.data.applications || []);
      setTotalPages(response.data.data.pagination?.pages || 1);
      setError(null);
    } catch (err) {
      setError('Failed to fetch applications');
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/social-tariff/admin/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setDetailsDialogOpen(true);
  };

  const handleReviewAction = (application, action) => {
    setSelectedApplication(application);
    setReviewData({
      reviewStatus: action,
      adminNotes: '',
      rejectionReason: action === 'DENIED' ? '' : ''
    });
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (!selectedApplication) return;
    
    if (reviewData.reviewStatus === 'DENIED' && !reviewData.rejectionReason.trim()) {
      setError('Rejection reason is required for denied applications');
      return;
    }

    try {
      setReviewLoading(true);
      await api.put(`/social-tariff/admin/applications/${selectedApplication.id}/review`, reviewData);
      
      setReviewDialogOpen(false);
      setSelectedApplication(null);
      setReviewData({ reviewStatus: '', adminNotes: '', rejectionReason: '' });
      
      // Refresh data
      await fetchApplications();
      await fetchStats();
      
      setError(null);
    } catch (err) {
      setError('Failed to submit review');
      console.error('Error submitting review:', err);
    } finally {
      setReviewLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'success';
    if (score >= 5) return 'warning';
    return 'error';
  };

  if (loading && !applications.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Social Tariff Applications
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Social Tariff Applications
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Applications
                </Typography>
                <Typography variant="h5">
                  {stats.overview.total}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pending Review
                </Typography>
                <Typography variant="h5" color="warning.main">
                  {stats.overview.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approved
                </Typography>
                <Typography variant="h5" color="success.main">
                  {stats.overview.approved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Recent (7 days)
                </Typography>
                <Typography variant="h5">
                  {stats.overview.recent}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Review Status</InputLabel>
              <Select
                value={filters.reviewStatus}
                label="Review Status"
                onChange={(e) => setFilters({ ...filters, reviewStatus: e.target.value })}
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  minWidth:"200px"
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="DENIED">Denied</MenuItem>
                <MenuItem value="FLAGGED">Flagged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Application Status</InputLabel>
              <Select
                value={filters.status}
                label="Application Status"
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                sx={{
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main',
                  },
                  minWidth:"200px"
                }}
              >
                <MenuItem value="">All</MenuItem>
                {Object.entries(applicationStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({ reviewStatus: '', status: '' });
                setPage(1);
              }}
              sx={{ minWidth: 140 }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Applications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Review Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((application) => (
              <TableRow key={application.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                      <PersonIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {application.firstName}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {application.user?.firstName} {application.user?.lastName}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{application.email}</TableCell>
                <TableCell>{application.country}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {application.status.map((status, index) => (
                      <Chip
                        key={index}
                        label={applicationStatusLabels[status] || status}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Chip
                    icon={<ScoreIcon />}
                    label={application.score}
                    color={getScoreColor(application.score)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={application.reviewStatus}
                    color={statusColors[application.reviewStatus]}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(application.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(application)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {application.reviewStatus === 'PENDING' && (
                      <>
                        <Tooltip title="Approve">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleReviewAction(application, 'APPROVED')}
                          >
                            <ApproveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleReviewAction(application, 'DENIED')}
                          >
                            <RejectIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Flag for Review">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleReviewAction(application, 'FLAGGED')}
                          >
                            <FlagIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Application Details</DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Personal Information
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Name:
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.firstName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Email:
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.email}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Country:
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.country}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Birth Year:
                    </Typography>
                    <Typography variant="body1">
                      {selectedApplication.birthYear}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Application Info
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Status Categories:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {selectedApplication.status.map((status, index) => (
                        <Chip
                          key={index}
                          label={applicationStatusLabels[status] || status}
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Score:
                    </Typography>
                    <Chip
                      icon={<ScoreIcon />}
                      label={selectedApplication.score}
                      color={getScoreColor(selectedApplication.score)}
                    />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      Review Status:
                    </Typography>
                    <Chip
                      label={selectedApplication.reviewStatus}
                      color={statusColors[selectedApplication.reviewStatus]}
                    />
                  </Box>
                </Grid>
              </Grid>

              {selectedApplication.freeText && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Additional Information
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">
                      {selectedApplication.freeText}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedApplication.proofImage && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Proof Document
                  </Typography>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <img
                      src={selectedApplication.proofImage}
                      alt="Proof document"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                      onLoad={(e) => {
                        e.target.nextSibling.style.display = 'none';
                      }}
                    />
                    <Box
                      sx={{
                        display: 'none',
                        p: 3,
                        border: '2px dashed #ccc',
                        borderRadius: 1,
                        textAlign: 'center',
                        color: 'text.secondary'
                      }}
                    >
                      <Typography variant="body2">
                        Unable to load proof document image
                      </Typography>
                      <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Please check if the image URL is accessible
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}

              {selectedApplication.adminNotes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Admin Notes
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'info.light' }}>
                    <Typography variant="body2">
                      {selectedApplication.adminNotes}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {selectedApplication.rejectionReason && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rejection Reason
                  </Typography>
                  <Paper sx={{ p: 2, bgcolor: 'error.light' }}>
                    <Typography variant="body2">
                      {selectedApplication.rejectionReason}
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Review Application: {selectedApplication?.firstName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Review Decision</InputLabel>
              <Select
                value={reviewData.reviewStatus}
                label="Review Decision"
                onChange={(e) => setReviewData({ ...reviewData, reviewStatus: e.target.value })}
              >
                <MenuItem value="APPROVED">Approve</MenuItem>
                <MenuItem value="DENIED">Deny</MenuItem>
                <MenuItem value="FLAGGED">Flag for Further Review</MenuItem>
              </Select>
            </FormControl>

            {reviewData.reviewStatus === 'DENIED' && (
              <TextField
                fullWidth
                label="Rejection Reason *"
                multiline
                rows={3}
                value={reviewData.rejectionReason}
                onChange={(e) => setReviewData({ ...reviewData, rejectionReason: e.target.value })}
                sx={{ mb: 3 }}
                required
              />
            )}

            <TextField
              fullWidth
              label="Admin Notes (Optional)"
              multiline
              rows={3}
              value={reviewData.adminNotes}
              onChange={(e) => setReviewData({ ...reviewData, adminNotes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReviewDialogOpen(false)}
            disabled={reviewLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleReviewSubmit}
            variant="contained"
            disabled={reviewLoading || !reviewData.reviewStatus}
          >
            {reviewLoading ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SocialTariffManager;